import axios from 'axios'
import Cookies from 'js-cookie'

const state = {
  playerData: null,
  loading: false,
  error: null,
  player: null
}

const mutations = {
  SET_PLAYER_DATA(state, data) {
    state.playerData = data
    state.loading = false
    state.error = null
    // Only log if there's actual player data
    if (data) {
      console.log('Player data:', state.playerData)
    }
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
    state.loading = false
  },
  SET_PLAYER(state, player) {
    state.player = player
  }
}

const actions = {
  async checkPlayerExists({ rootState }) {
    try {
      if (!rootState.auth.userId) return false
      
      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/api/player/has/${rootState.auth.userId}`,
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      )
      return response.data.exists
    } catch (error) {
      console.error(error)
      return false
    }
  },

  async createNewPlayer({ rootState }, { companyName, companyType, identifier }) {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/api/player/create`,
        {
          userId: rootState.auth.userId,
          companyName,
          companyType,
          identifier
        },
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      )
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create player')
    }
  },

  async updatePlayerCoins({ commit, state }, newCoins) {
    try {
      const response = await axios.put(
        `${process.env.VUE_APP_API_URL}/api/player/${state.playerData.id}`,
        { coins: newCoins },
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      )
      commit('SET_PLAYER_DATA', response.data)
    } catch (error) {
      console.error('Failed to update coins:', error)
      throw error
    }
  },

  async loadPlayerData({ commit, dispatch, rootState }) {
    commit('SET_LOADING', true)
    try {
      if (!rootState.auth.userId) {
        return null
      }

      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/api/player/data/${rootState.auth.userId}`,
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      )
      
      commit('SET_PLAYER_DATA', response.data)
      
      // After loading player data, check for revival status
      if (response.data?.id) {
        try {
          await dispatch('revival/checkRevivalStatus', null, { root: true });
        } catch (revivalError) {
          console.warn('Revival check failed:', revivalError);
          // Don't fail the login process if revival check fails
        }
      }
      
      return response.data
    } catch (error) {
      // Silently handle 400 errors (no player profile)
      if (error.response?.status === 400) {
        commit('SET_PLAYER_DATA', null)
        return null
      }
      // For other errors, still set error state but don't throw
      commit('SET_ERROR', error.message)
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchPlayer({ commit, rootState }) {
    try {
      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/api/player/data/${rootState.auth.userId}`,
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      );
      commit('SET_PLAYER', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching player:', error);
      commit('SET_ERROR', 'Erro ao carregar dados do jogador');
      return null;
    }
  },

  async createPlayerProfile({ rootState }) {
    const token = rootState.auth.token
    if (!token) throw new Error('No authentication token')

    const response = await axios.post(
      `${process.env.VUE_APP_API_URL}/api/player/create`,
      {
        userId: rootState.auth.userId,
        // Default values for new players
        coins: 1000,
        level: 1,
        experience: 0,
        inventory: {
          items: []
        }
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    )
    return response.data
  },

  async createPlayerAfterAuth({ dispatch, rootState }, { companyName, companyType, identifier }) {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/api/player/create-after-auth`,
        {
          userId: rootState.auth.userId,
          companyName,
          companyType,
          identifier
        },
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      )
      
      // If successful, load player data to update state
      if (response.data.success) {
        await dispatch('loadPlayerData')
      }
      
      return response.data
    } catch (error) {
      console.error('Error creating player after auth:', error)
      throw new Error(error.response?.data?.message || 'Failed to create player')
    }
  },
  
  async findPlayerByNameOrId({ commit }, search) {
    try {
      commit('SET_LOADING', true);
      const response = await axios.get(
        `${process.env.VUE_APP_API_URL}/api/player/find/${search}`,
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      );
      commit('SET_LOADING', false);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to find player');
      commit('SET_LOADING', false);
      throw error;
    }
  },

  async processWeeklyFinancials({ commit, state }) {
    try {
      // Check if player data exists
      if (!state.playerData || !state.playerData.id) {
        console.error('Cannot process weekly financials: Player data not available');
        return null;
      }
      
      const response = await axios.put(
        `${process.env.VUE_APP_API_URL}/api/player/${state.playerData.id}/process-weekly-financials`,
        {},
        {
          headers: { Authorization: `Bearer ${Cookies.get('jwt')}` }
        }
      );
      
      // Log the response for debugging
      console.log('Weekly financials response:', response.data);
      
      // If no weekly financials processing needed (already processed)
      if (!response.data.success) {
        return null;
      }
      
      // Return the financial details for the modal to display
      return response.data;
    } catch (error) {
      console.error('Error processing weekly financials:', error);
      commit('SET_ERROR', 'Failed to process weekly financials');
      return null;
    }
  }
}

const getters = {
  player: state => state.playerData,
  currentPlayer: state => state.playerData, // Alias for compatibility with PremiumStore
  playerData: state => state.playerData,
  playerInventory: state => state.playerData?.inventory?.items || []
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
